import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { NavigateFunction } from "react-router-dom";
import { isApiError } from "@/utils/errorUtils";
import { useQueryClient } from "@tanstack/react-query";
import { toaster } from "@/components/ui/toaster";

type UseSubmissionProps = {
    isSubmitSuccess: boolean;
    navigate: NavigateFunction;
    invoiceId: string | undefined;
    isSubmitError: boolean;
    submitErrorMessage?: unknown;
    broadcast: (msg: { type: string; invoiceId: string }) => void;
};

const STATUS_OK = 200;

const useSubmission = ({
    isSubmitSuccess,
    navigate,
    invoiceId,
    isSubmitError,
    submitErrorMessage,
    broadcast
}: UseSubmissionProps) => {
    const { t } = useTranslation("invoices");
    const queryClient = useQueryClient();

    useEffect(() => {
        if (isSubmitSuccess) {
            broadcast({
                type: "INVOICE_SUBMITTED",
                invoiceId: invoiceId ?? ""
            });
            toaster.create({
                id: "invoiceSubmitted",
                title: t("invoices:createInvoice.invoiceSubmitted"),
                type: "success",
                closable: true
            });
            queryClient.invalidateQueries({ queryKey: ["invoice", invoiceId] });
            queryClient.invalidateQueries({ queryKey: ["invoices"] });
            navigate("/", { state: { submittedInvoiceId: invoiceId } });
            return;
        }

        if (!isSubmitError) {
            return;
        }

        const baseFailure = t("invoices:createInvoice.invoiceSubmitFailed");
        const detail = getUnprocessableStatusDetail(submitErrorMessage);

        toaster.create({
            id: detail ? "invoiceSubmitFailedDetailed" : "invoiceSubmitFailed",
            title: detail ? `${baseFailure}: ${detail}` : baseFailure,
            type: "error",
            closable: true
        });
    }, [broadcast, invoiceId, isSubmitSuccess, isSubmitError, submitErrorMessage, navigate, queryClient, t]);
};

interface ApiErrorWithStatus {
    status?: number;
    message?: string;
    detail?: string;
}

// Returns a user-facing detail string if error is non-OK; otherwise undefined
const getUnprocessableStatusDetail = (error: unknown): string | undefined => {
    if (!error) {
        return undefined;
    }
    if (isApiError(error)) {
        const { status, detail, message } = error as ApiErrorWithStatus;
        if (status !== STATUS_OK) {
            // Prefer explicit detail, fall back to message, else generic code
            return detail ?? message ?? `HTTP ${status}`;
        }
    }
    return undefined;
};

export default useSubmission;
